import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../axios/axiosInstance';
import { ExamTemplate } from 'model/response';
import { useEffect, useRef, useState } from 'react';
import { Input, message, Popconfirm, Select, Table } from 'antd';
import { useMasterSchoolYearsSelector, useMasterSubjectsSelector } from '../../../../redux/selector';
import { ColumnsType } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store/store';
import { updateLoading } from '../../../../redux/slice/appConfigSlice';
import { setExamTemplateRequest } from '../../../../redux/slice/examTemplateEditSlice';
import { cloneDeep } from 'lodash';
import { ExamTemplatePartRequest, ExamTemplateQuestionRequest, ExamTemplateRequest } from 'model/request';
import processError from '../../../../utils/processError';
import useTableConfig from '../../../../hooks/useTableConfig';
import { mapToSelectItems } from 'utils/utils';

export default function TestTemplateManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const navigate = useNavigate();
  const schoolYearList = useMasterSchoolYearsSelector();
  const subjects = useMasterSubjectsSelector();
  const [subjectId, setSubjectId] = useState('');
  const [keyword, setKeyword] = useState<string>('');
  const [schoolYearFilter, setSchoolYearFilter] = useState<string>('');
  const rootElement: any = useRef(null);
  const dispatch = useDispatch<AppDispatch>();

  const [data, setData] = useState<ExamTemplate[]>();
  const [loading, setLoading] = useState(false);

  const columns: ColumnsType<ExamTemplate> = [
    {
      title: 'Test Đề thi',
      dataIndex: 'name',
      width: '60%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'Năm',
      dataIndex: 'schoolYear.code',
      render: (_, record) => (record.schoolYear ? record.schoolYear.code : ''),
      width: '30%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('schoolYear.code'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      width: '10%',
      render: (action, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa template đề thi"
            description="Xác nhận xóa template đề thi. Hành động này không thể hoàn tác?"
            onConfirm={() => deleteExamTemplate(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deleteExamTemplate = async (record: ExamTemplate) => {
    setLoading(true);

    try {
      await axiosInstance({
        url: `/exam-templates/${record.id}`,
        method: 'delete',
      });
      message.success('Xoá thành công');
      await loadListExamTemplates();
    } catch (err) {
      processError(err);
    }
    setLoading(false);
  };

  const moveEditView = async (e: any, record: ExamTemplate) => {
    dispatch(updateLoading(true));
    try {
      const res = await axiosInstance({
        url: `exam-templates/${record.id}:get-full-detail`,
        method: 'get',
      });

      if (res.status === 200) {
        const formatData = cloneDeep(res.data) as ExamTemplateRequest;
        const sortExamTemplateQuestion = (
          examTemplateQuestions: ExamTemplateQuestionRequest[],
        ): ExamTemplateQuestionRequest[] => {
          if (!examTemplateQuestions || (examTemplateQuestions && examTemplateQuestions.length === 0)) {
            return [];
          }

          let sortedExamTemplateQuestions = examTemplateQuestions.sort((e1, e2) => e1.questionIndex - e2.questionIndex);

          for (let childExamTemplateQuestion of sortedExamTemplateQuestions) {
            childExamTemplateQuestion.children = sortExamTemplateQuestion(childExamTemplateQuestion.children);
          }

          return sortedExamTemplateQuestions;
        };

        const sortExamTemplatePart = (examTemplateParts: ExamTemplatePartRequest[]): ExamTemplatePartRequest[] => {
          if (!examTemplateParts || (examTemplateParts && examTemplateParts.length === 0)) {
            return [];
          }

          let sortedExamTemplateParts = examTemplateParts.sort((e1, e2) => e1.partIndex - e2.partIndex);

          for (let childExamTemplatePart of sortedExamTemplateParts) {
            childExamTemplatePart.children = sortExamTemplatePart(childExamTemplatePart.children);
            childExamTemplatePart.examTemplateQuestions = sortExamTemplateQuestion(
              childExamTemplatePart.examTemplateQuestions,
            );
          }

          return sortedExamTemplateParts;
        };

        formatData.examTemplateParts = sortExamTemplatePart(formatData.examTemplateParts);

        dispatch(setExamTemplateRequest(formatData));
        navigate(`/master-setting/edit-exam-template/${record.id}`);
      } else {
        message.error('Có lỗi xảy ra.');
      }
    } catch (err) {
      processError(err);
    }

    dispatch(updateLoading(false));
  };

  const loadListExamTemplates = async function () {
    setLoading(true);
    const res = await axiosInstance({
      url: '/exam-templates/page',
      params: {
        keyword,
        size: pageSize,
        page: currentPage,
        sortBy,
        direction,
        subjectId,
        schoolYearId: schoolYearFilter,
        fromCMS: true,
      },
    });

    if (res.status === 200) {
      setData(res.data.content);
      setLoading(false);
      setTotalElements(res.data.totalElements);
    }
  };

  const onChangeSelect = function (e: any, fieldName: string) {
    switch (fieldName) {
      case 'schoolYear':
        setSchoolYearFilter(e);
        break;
      default:
        break;
    }
  };

  const onSearch = function (e: any) {
    setKeyword(e.target.value);
  };

  const addNewExamTemplate = async () => {
    try {
      const res = await axiosInstance({
        url: '/exam-templates/build',
        method: 'get',
      });

      if (res.status === 200) {
        dispatch(setExamTemplateRequest(res.data));
        navigate('/master-setting/edit-exam-template');
      } else {
        message.error('Có lỗi xảy ra.');
      }
    } catch (err) {
      processError(err);
    }
  };

  useEffect(() => {
    loadListExamTemplates().then();
  }, [currentPage, pageSize, sortBy, direction, schoolYearFilter, keyword, subjectId]);

  return (
    <div className="w-full h-full p-4" ref={rootElement}>
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={addNewExamTemplate}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
          <div className="min-w-[170px] max-w-[170px] flex items-center gap-2">
            <div>Năm</div>
            <Select
              onChange={(e) => onChangeSelect(e, 'schoolYear')}
              className="w-full"
              placeholder="Bộ lọc-Năm học"
              style={{ flex: 1 }}
              value={schoolYearFilter}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(
                schoolYearList.map((schoolYear) => ({
                  value: schoolYear.id,
                  label: schoolYear.code,
                })),
              )}
            />
          </div>
          <div className="min-w-[300px] max-w-[300px] flex items-center gap-2">
            <div className="w-[100px]">Môn học</div>
            <Select
              onChange={(e) => setSubjectId(e)}
              className="w-full min-w-[200px]"
              placeholder="Bộ lọc - Môn học"
              style={{ flex: 1 }}
              value={subjectId}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(mapToSelectItems(subjects))}
            />
          </div>
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onPressEnter={(e) => onSearch(e)} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          scroll={{ y: (window.innerHeight * 52) / 100 }}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
}
